import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { clientPortalApi } from "./clientPortalApi";
import { projectYear } from "./clientStatusLabels";
import { useDocumentTitle } from "./useDocumentTitle";
import { Search } from "lucide-react";
import "./clientPortal.css";

/** Client Portal v2 (2026-09, Phase 1, §3) — "Projects archive". Replaces the v1 flat card
 * grid with a searchable/filterable list (search by name, Active/Completed, year, category) —
 * every filter runs client-side over the one already-fetched list, no new endpoints. */
export default function ClientPortalProjectsList() {
  useDocumentTitle("Projects");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState("all");
  const [year, setYear] = useState("all");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    clientPortalApi.listProjects().then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  const years = useMemo(() => Array.from(new Set(items.map(projectYear).filter(Boolean))).sort().reverse(), [items]);
  const categories = useMemo(() => Array.from(new Set(items.map((p) => p.category).filter(Boolean))).sort(), [items]);

  const filtered = useMemo(() => items.filter((p) => {
    if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (bucket === "active" && !p.is_active) return false;
    // 2026-06 (Tessolve audit): "Completed" is the server's explicit `is_previous`
    // (Delivered/Archived), never `!is_active` — which used to sweep a pre-sale/hidden project
    // reaching a staff preview session into the client's Completed filter.
    if (bucket === "completed" && !p.is_previous) return false;
    if (year !== "all" && projectYear(p) !== year) return false;
    if (category !== "all" && p.category !== category) return false;
    return true;
  }), [items, q, bucket, year, category]);

  if (loading) {
    return <div className="cp-theme min-h-[60vh] flex items-center justify-center text-[color:var(--cp-grey-3)] text-sm" data-testid="client-projects-loading">Loading projects…</div>;
  }

  return (
    <div className="cp-theme cp-enter max-w-[1200px] mx-auto px-4 md:px-10 py-9 md:py-12" data-testid="client-portal-projects-archive">
      <h1 className="cp-display text-[26px] md:text-[32px]">Projects</h1>
      <p className="mt-1.5 text-[13.5px] text-[color:var(--cp-grey-3)]">Every project NJM has run with you, active or wrapped.</p>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[color:var(--cp-grey-4)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects"
            className="cp-input-light w-full pl-9 pr-3 py-2.5 text-[13px]" data-testid="client-projects-search-input" />
        </div>
        <select value={bucket} onChange={(e) => setBucket(e.target.value)} className="cp-input-light px-3 py-2.5 text-[12px] font-semibold uppercase tracking-wide" data-testid="client-projects-filter-bucket">
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
        {years.length > 0 && (
          <select value={year} onChange={(e) => setYear(e.target.value)} className="cp-input-light px-3 py-2.5 text-[12px] font-semibold uppercase tracking-wide" data-testid="client-projects-filter-year">
            <option value="all">Any year</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {categories.length > 0 && (
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="cp-input-light px-3 py-2.5 text-[12px] font-semibold uppercase tracking-wide" data-testid="client-projects-filter-category">
            <option value="all">Any category</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      <div className="mt-6 cp-section-rule">
        {filtered.length === 0 ? (
          <p className="py-10 text-sm text-[color:var(--cp-grey-3)]" data-testid="client-projects-empty">No projects match those filters.</p>
        ) : (
          filtered.map((p) => (
            <Link key={p.id} to={`/client-portal/projects/${p.id}`} className="cp-row-divider flex flex-col md:flex-row md:items-center gap-2 py-4 hover:bg-black/[.02] transition-colors -mx-2 px-2" data-testid={`client-projects-row-${p.id}`}>
              <div className="flex items-center gap-2 md:w-[300px] shrink-0">
                <span className={`cp-status-dot ${p.is_active ? "" : "cp-status-dot-done"}`} />
                <span className="font-semibold text-[14px]">{p.name}</span>
              </div>
              <div className="flex-1 text-[12px] text-[color:var(--cp-grey-3)]">{p.category || "—"}{projectYear(p) ? ` · ${projectYear(p)}` : ""}</div>
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-3)] shrink-0">{p.status_label}</div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
